import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// A deliberately small i18n layer (no react-i18next): the app has two UI
// languages and every string is written as { tr, en } side by side in the
// component's messages file, so a missing translation is visible in review
// rather than discovered at runtime.
//
//   const messages = { title: { tr: 'Gruplar', en: 'Groups' },
//                      count: { tr: '{n} grup', en: '{n} groups' } };
//   const { t } = useT(messages);
//   t('count', { n: 3 })
//
// A value may also be a function (vars) => node, for strings that need JSX
// (bold words, links) or real plural rules.

export const SUPPORTED_LANGS = ['tr', 'en'];
const STORAGE_KEY = 'sm_lang';
const DATE_LOCALES = { tr: 'tr-TR', en: 'en-GB' };

function readStoredLang() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LANGS.includes(v) ? v : null;
  } catch (e) {
    return null;
  }
}

function writeStoredLang(lang) {
  try { window.localStorage.setItem(STORAGE_KEY, lang); } catch (e) { /* private mode */ }
}

// ?lang=en (shareable links) > the visitor's own earlier choice > browser
// language. Anything that isn't Turkish gets English.
function detectLang() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (SUPPORTED_LANGS.includes(fromUrl)) {
      writeStoredLang(fromUrl);
      return fromUrl;
    }
  } catch (e) { /* no window.location in tests */ }
  const stored = readStoredLang();
  if (stored) return stored;
  const prefs = (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || [];
  return prefs.some(l => (l || '').toLowerCase().startsWith('tr')) ? 'tr' : 'en';
}

// Module-level mirror of the current language for code outside React
// (constants helpers, api error fallbacks).
let currentLang = typeof window !== 'undefined' ? detectLang() : 'tr';
export const getLang = () => currentLang;
export const getDateLocale = (lang = currentLang) => DATE_LOCALES[lang] || DATE_LOCALES.tr;

function interpolate(str, vars) {
  if (!vars || typeof str !== 'string') return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : m));
}

// Resolve one { tr, en } entry for a language — usable outside React too.
export function pick(entry, vars, lang = currentLang) {
  if (entry === undefined || entry === null) return '';
  if (typeof entry !== 'object' || React.isValidElement(entry)) return entry;
  const value = entry[lang] !== undefined ? entry[lang] : entry.tr;
  return typeof value === 'function' ? value(vars || {}) : interpolate(value, vars);
}

const I18nContext = createContext({ lang: currentLang, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(currentLang);

  const setLang = useCallback((next) => {
    if (!SUPPORTED_LANGS.includes(next)) return;
    currentLang = next;
    writeStoredLang(next);
    setLangState(next);
  }, []);

  useEffect(() => {
    currentLang = lang;
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const { lang, setLang } = useContext(I18nContext);
  const locale = getDateLocale(lang);
  return {
    lang,
    setLang,
    locale,
    formatDate: (d, opts) => (d ? new Date(d).toLocaleDateString(locale, opts) : ''),
    formatDateTime: (d, opts) => (d ? new Date(d).toLocaleString(locale, opts) : ''),
    formatNumber: (n, opts) => (typeof n === 'number' ? n.toLocaleString(locale, opts) : n),
  };
}

// t(key, vars) for a component's own messages object, falling back to the
// shared `common` messages, then to the key itself.
const NO_MESSAGES = {};

export function useT(messages = NO_MESSAGES) {
  const i18n = useI18n();
  const { lang } = i18n;
  const t = useCallback((key, vars) => {
    const entry = messages[key] !== undefined ? messages[key] : common[key];
    if (entry === undefined) return key;
    return pick(entry, vars, lang);
  }, [messages, lang]);
  return { ...i18n, t };
}

// Strings used across many screens.
export const common = {
  save: { tr: 'Kaydet', en: 'Save' },
  saving: { tr: 'Kaydediliyor...', en: 'Saving...' },
  cancel: { tr: 'İptal', en: 'Cancel' },
  close: { tr: 'Kapat', en: 'Close' },
  delete: { tr: 'Sil', en: 'Delete' },
  deleting: { tr: 'Siliniyor...', en: 'Deleting...' },
  edit: { tr: 'Düzenle', en: 'Edit' },
  create: { tr: 'Oluştur', en: 'Create' },
  update: { tr: 'Güncelle', en: 'Update' },
  add: { tr: 'Ekle', en: 'Add' },
  search: { tr: 'Ara', en: 'Search' },
  loading: { tr: 'Yükleniyor...', en: 'Loading...' },
  yes: { tr: 'Evet', en: 'Yes' },
  no: { tr: 'Hayır', en: 'No' },
  back: { tr: 'Geri', en: 'Back' },
  next: { tr: 'İleri', en: 'Next' },
  previous: { tr: 'Önceki', en: 'Previous' },
  active: { tr: 'Aktif', en: 'Active' },
  inactive: { tr: 'Pasif', en: 'Inactive' },
  actions: { tr: 'İşlemler', en: 'Actions' },
  status: { tr: 'Durum', en: 'Status' },
  name: { tr: 'Ad', en: 'Name' },
  email: { tr: 'E-posta', en: 'Email' },
  date: { tr: 'Tarih', en: 'Date' },
  all: { tr: 'Tümü', en: 'All' },
  none: { tr: 'Yok', en: 'None' },
  preview: { tr: 'Önizleme', en: 'Preview' },
  refresh: { tr: 'Yenile', en: 'Refresh' },
  genericError: { tr: 'Bir hata oluştu.', en: 'Something went wrong.' },
  loadError: { tr: 'Veriler yüklenemedi.', en: 'Could not load data.' },
};

// Backend error strings are Turkish (the API has no i18n yet). In Turkish
// show them as-is — they're the most specific message there is; in English
// fall back to the screen's own localized message rather than mixing
// languages.
export function apiErrorMessage(err, fallbackEntry, lang = currentLang) {
  const fallback = pick(fallbackEntry, undefined, lang);
  if (lang !== 'tr') return fallback;
  const data = err && err.response && err.response.data;
  return (data && (data.error || data.detail)) || fallback;
}
